import { Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTopDonors } from '../context/TopDonorsContext';
import { navigateToPublicProfile } from '../utils/profileUtils';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useEffect } from 'react';

const formatAmount = (amount: number): JSX.Element => {
  // Version complète pour PC
  const getFullAmount = () => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M FCFA`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K FCFA`;
    }
    return `${amount} FCFA`;
  };

  // Version tronquée pour mobile
  const getTruncatedAmount = () => {
    let formattedAmount: string;
    if (amount >= 1000000) {
      formattedAmount = `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      formattedAmount = `${(amount / 1000).toFixed(1)}K`;
    } else {
      formattedAmount = amount.toString();
    }

    if (formattedAmount.length > 8) {
      return formattedAmount.substring(0, 8) + '...';
    }
    return formattedAmount + ' FCFA';
  };

  return (
    <span className="text-green-400 font-medium">
      <span className="hidden md:inline">{getFullAmount()}</span>
      <span className="inline md:hidden">{getTruncatedAmount()}</span>
    </span>
  );
};

export function Top() {
  const { donors, topLimit, loading } = useTopDonors();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-400'; // Or
      case 2:
        return 'text-gray-400'; // Argent
      case 3:
        return 'text-amber-600'; // Bronze
      default:
        return 'text-gray-300';
    }
  };

  const handleDonorClick = async (donorId: string) => {
    navigateToPublicProfile(donorId, navigate);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="fixed top-[64px] left-0 right-0 bottom-0 bg-black">
      <div className="container mx-auto px-4 pt-6">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="w-6 h-6 text-yellow-400" />
          <h1 className="text-lg font-bold text-white">
            TOP {topLimit} de la Générosité
          </h1>
        </div>

        <div className="space-y-2 h-[calc(100vh-220px)] overflow-y-auto">
          {donors.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              Aucun donateur pour le moment
            </p>
          ) : (
            donors.map((donor) => (
              <div
                key={donor.id}
                onClick={() => handleDonorClick(donor.id)}
                className="block bg-white/5 backdrop-blur-lg rounded-lg p-4 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className={`text-xl font-bold ${getRankColor(donor.rank)}`}>
                      #{donor.rank}
                    </span>
                    <span className="text-white">{donor.username}</span>
                  </div>
                  <div>
                    {formatAmount(donor.totalDonations)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
} 